import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ConfirmDialog } from '../../../shared/components/ConfirmDialog';
import { useToast } from '../../../shared/stores/toast.store';
import { describeApiError } from '../../../shared/api/errors';
import { useEmployeeNotasFiscaisHooks } from '../notas-fiscais/hooks';
import { EmployeeNotaFiscalFormDialog } from '../notas-fiscais/EmployeeNotaFiscalFormDialog';
import { NotaFiscalUploadDialog } from '../notas-fiscais/NotaFiscalUploadDialog';
import { NotaFiscalReprovarDialog } from '../notas-fiscais/NotaFiscalReprovarDialog';
import { InformarPagamentoDialog } from '../notas-fiscais/InformarPagamentoDialog';
import { NotaFiscalColaboradorMostrarDialog } from './NotaFiscalColaboradorMostrarDialog';
import { NotaFiscalColaboradorExcluirDialog } from './NotaFiscalColaboradorExcluirDialog';
import type { NotaFiscalAcao } from './NotaFiscalColaboradorAcoesMenu';
import type { NotaFiscalGlobalListItem } from './types';

export interface NotaFiscalAcoesDialogProps {
  nota: NotaFiscalGlobalListItem | null;
  acao: NotaFiscalAcao | null;
  onClose: () => void;
}

/**
 * Abre só o diálogo da ação escolhida no menu da grade. A lista global é
 * atualizada ao fechar, porque o status da nota pode ter mudado.
 */
export function NotaFiscalAcoesDialog({
  nota,
  acao,
  onClose,
}: NotaFiscalAcoesDialogProps): JSX.Element | null {
  const { t } = useTranslation();
  const toast = useToast();
  const qc = useQueryClient();
  const employeeId = nota?.employeeId ?? 0;
  const hooks = useEmployeeNotasFiscaisHooks(employeeId);
  const detailQuery = hooks.useDetail(acao === 'editar' && nota ? nota.id : null);
  const solicitarMut = hooks.useSolicitar();
  const aprovarMut = hooks.useAprovar();
  const deleteMut = hooks.useDelete();

  if (!nota || !acao) return null;

  const current = detailQuery.data ?? nota;

  function invalidateGlobal(): void {
    qc.invalidateQueries({ queryKey: ['notas-fiscais', 'global'] });
  }
  function handleClose(): void {
    invalidateGlobal();
    onClose();
  }

  async function confirmSolicitar(): Promise<void> {
    try {
      await solicitarMut.mutateAsync(current.id);
      toast.success(t('features.colaboradores.notasFiscais.toasts.solicitada'));
      handleClose();
    } catch (err) {
      toast.error(describeApiError(err));
    }
  }
  async function confirmAprovar(): Promise<void> {
    try {
      await aprovarMut.mutateAsync(current.id);
      toast.success(t('features.colaboradores.notasFiscais.toasts.aprovada'));
      handleClose();
    } catch (err) {
      toast.error(describeApiError(err));
    }
  }
  async function confirmDelete(): Promise<void> {
    try {
      await deleteMut.mutateAsync(current.id);
      toast.success(t('features.colaboradores.notasFiscais.toasts.deleted'));
      handleClose();
    } catch (err) {
      toast.error(describeApiError(err));
    }
  }

  const A = 'features.colaboradores.notasFiscaisGlobal.acoes';

  return (
    <>
      <NotaFiscalColaboradorMostrarDialog
        open={acao === 'mostrar'}
        nota={nota}
        onClose={onClose}
      />

      {acao === 'editar' && detailQuery.data && (
        <EmployeeNotaFiscalFormDialog
          open
          nota={detailQuery.data}
          hooks={{ useCreate: hooks.useCreate, useUpdate: hooks.useUpdate }}
          onClose={handleClose}
        />
      )}

      <NotaFiscalUploadDialog
        open={acao === 'upload'}
        nota={current}
        useUploadFile={hooks.useUploadFile}
        onClose={handleClose}
      />

      <NotaFiscalReprovarDialog
        open={acao === 'reprovar'}
        nota={current}
        useReprovar={hooks.useReprovar}
        onClose={handleClose}
      />

      <InformarPagamentoDialog
        open={acao === 'pagamento'}
        employeeId={employeeId}
        notaFiscalId={current.id}
        colaboradorNome={current.colaboradorNome}
        clienteNome={current.clienteNome}
        periodo={current.periodo}
        valorReceber={current.valorReceberFormula ?? 0}
        onClose={handleClose}
      />

      <ConfirmDialog
        open={acao === 'solicitar'}
        title={t('features.colaboradores.notasFiscais.solicitarConfirm.title')}
        description={t(
          current.status === 'NSO'
            ? 'features.colaboradores.notasFiscais.solicitarConfirm.descriptionNSO'
            : 'features.colaboradores.notasFiscais.solicitarConfirm.descriptionResolicitar',
          { periodo: current.periodo, cliente: current.clienteNome },
        )}
        confirmLabel={t(`${A}.solicitar`)}
        loading={solicitarMut.isPending}
        onConfirm={confirmSolicitar}
        onCancel={onClose}
      />
      <ConfirmDialog
        open={acao === 'aprovar'}
        title={t('features.colaboradores.notasFiscais.aprovarConfirm.title')}
        description={t('features.colaboradores.notasFiscais.aprovarConfirm.description', {
          periodo: current.periodo,
          cliente: current.clienteNome,
        })}
        confirmLabel={t(`${A}.aprovar`)}
        loading={aprovarMut.isPending}
        onConfirm={confirmAprovar}
        onCancel={onClose}
      />
      <NotaFiscalColaboradorExcluirDialog
        open={acao === 'excluir'}
        nota={nota}
        loading={deleteMut.isPending}
        onConfirm={confirmDelete}
        onClose={onClose}
      />
    </>
  );
}
